import {
  AudioOutlined,
  CopyOutlined,
  MenuOutlined,
  ReloadOutlined,
  RobotOutlined,
  StopOutlined,
} from "@ant-design/icons";
import type { UIMessage } from "ai";
import { Alert, Button, Card, Space, Tag, Tooltip, Typography, message } from "antd";
import type React from "react";
import { useState } from "react";

import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton,
} from "@fitness-app/web-ui/components/ai-elements/conversation";
import { Loader } from "@fitness-app/web-ui/components/ai-elements/loader";
import {
  Message,
  MessageAction,
  MessageActions,
  MessageContent,
} from "@fitness-app/web-ui/components/ai-elements/message";
import {
  PromptInput,
  PromptInputActionAddAttachments,
  PromptInputActionMenu,
  PromptInputActionMenuContent,
  PromptInputActionMenuTrigger,
  PromptInputBody,
  type PromptInputFile,
  PromptInputFooter,
  PromptInputTextarea,
  PromptInputTools,
} from "@fitness-app/web-ui/components/ai-elements/prompt-input";
import { Suggestion, Suggestions } from "@fitness-app/web-ui/components/ai-elements/suggestion";

import { getSalesAgentModelOption, type SalesAgentModelOptionId, salesAgentSuggestions } from "./constants";
import { getMessageText } from "./messageUtils";
import { SalesAgentMessageParts, SalesAgentPromptHeader, SalesAgentPromptSubmit } from "./SalesAgentMessageParts";
import { SalesAgentModelPicker } from "./SalesAgentModelPicker";
import { SalesAgentVoiceRecorder } from "./SalesAgentVoiceRecorder";
import { useTranslation as useInterfaceTranslation } from "react-i18next";

type ChatStatus = "ready" | "submitted" | "streaming" | "error";

type SalesAgentChatCardProps = {
  activeSessionTitle: string;
  chatError?: Error;
  chatMessages: UIMessage[];
  chatStatus: ChatStatus;
  enabledSkillsCount: number;
  input: string;
  isChatBusy: boolean;
  isViewingOwnChats: boolean;
  onClearError: () => void;
  onCopyMessage: (message: UIMessage) => void;
  onModelChange: (modelId: SalesAgentModelOptionId) => void;
  onOpenPanel: () => void;
  onRetryLastResponse: () => void;
  onStop: () => void;
  onSubmitMessage: (text: string, files?: PromptInputFile[]) => void;
  promptTextareaRef: React.RefObject<HTMLTextAreaElement | null>;
  selectedModelId: SalesAgentModelOptionId;
  setInput: (value: string) => void;
  shouldShowMessagesLoader: boolean | string | null;
};

export const SalesAgentChatCard = ({
  activeSessionTitle,
  chatError,
  chatMessages,
  chatStatus,
  enabledSkillsCount,
  input,
  isChatBusy,
  isViewingOwnChats,
  onClearError,
  onCopyMessage,
  onModelChange,
  onOpenPanel,
  onRetryLastResponse,
  onStop,
  onSubmitMessage,
  promptTextareaRef,
  selectedModelId,
  setInput,
  shouldShowMessagesLoader,
}: SalesAgentChatCardProps) => {
  const { t: tUi } = useInterfaceTranslation();
  const selectedModelOption = getSalesAgentModelOption(selectedModelId);
  const [audioRecordMode, setAudioRecordMode] = useState(false);

  const handlePromptKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;

    event.preventDefault();
    event.currentTarget.form?.requestSubmit();
  };

  return (
    <Card
      className="h-full min-h-0 overflow-hidden rounded-lg border border-gray-200 shadow-sm"
      styles={{ body: { height: "100%", minHeight: 0, overflow: "hidden", padding: 0 } }}
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="shrink-0 border-b border-gray-100 px-5 py-4">
          {/* Flex z zawijaniem zamiast Space: Space nie zawija, więc na wąskim ekranie
              ściskał tytuł do jednej litery w kolumnie. */}
          <div className="flex w-full flex-wrap items-start justify-between gap-x-4 gap-y-2">
            <div className="min-w-0 flex-[1_1_200px]">
              <Typography.Text className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Rozmowa sprzedażowa
              </Typography.Text>
              <Typography.Title level={4} className="!mb-0 !mt-1 break-words">
                {activeSessionTitle}
              </Typography.Title>
            </div>
            <Space wrap size={[8, 8]}>
              <Tag color="green">{enabledSkillsCount} aktywnych skillów</Tag>
              <Tag color="blue">
                {selectedModelOption.label} · {selectedModelOption.reasoningEffort}
              </Tag>
              <Button className="xl:hidden" icon={<MenuOutlined />} onClick={onOpenPanel}>
                Historia i skille
              </Button>
            </Space>
          </div>
        </div>

        <Conversation>
          <ConversationContent className="mx-auto w-full max-w-5xl px-6 pb-4">
            {shouldShowMessagesLoader ? (
              <div className="py-12 text-center text-sm text-gray-500">Ładowanie rozmowy...</div>
            ) : chatMessages.length ? (
              chatMessages.map((item, index) => (
                <div key={item.id} className="w-full">
                  <Message from={item.role}>
                    <MessageContent
                      className={
                        item.role === "user"
                          ? "rounded-[22px] border-transparent bg-gray-100 px-4 py-2.5 text-gray-950 shadow-none"
                          : "border-transparent bg-transparent px-0 py-0 text-gray-900 shadow-none"
                      }
                    >
                      <SalesAgentMessageParts
                        message={item}
                        isLastMessage={index === chatMessages.length - 1}
                        isStreaming={chatStatus === "streaming"}
                      />
                    </MessageContent>
                  </Message>
                  {item.role === "assistant" && getMessageText(item).trim() ? (
                    <MessageActions className="ml-1">
                      <MessageAction label="Kopiuj odpowiedź" onClick={() => onCopyMessage(item)}>
                        <CopyOutlined />
                      </MessageAction>
                      {index === chatMessages.length - 1 ? (
                        <MessageAction label="Wygeneruj ponownie" disabled={isChatBusy} onClick={onRetryLastResponse}>
                          <ReloadOutlined />
                        </MessageAction>
                      ) : null}
                    </MessageActions>
                  ) : null}
                </div>
              ))
            ) : (
              <ConversationEmptyState
                icon={<RobotOutlined className="text-2xl" />}
                title="Nowa rozmowa"
                description="Opisz sytuację z rozmowy sprzedażowej. Mogę pomóc w diagnozie, obiekcjach, domknięciu albo przygotowaniu gotowej odpowiedzi do klientki."
              />
            )}
            {chatStatus === "submitted" ? (
              <Message from="assistant">
                <MessageContent className="border-transparent bg-transparent px-0 py-0 text-gray-500 shadow-none">
                  <Loader />
                </MessageContent>
              </Message>
            ) : null}
          </ConversationContent>
          <ConversationScrollButton />
        </Conversation>

        <div className="shrink-0 bg-transparent px-4 pb-4 pt-0">
          {chatMessages.length === 0 && isViewingOwnChats ? (
            <Suggestions className="mx-auto mb-3 max-w-5xl">
              {salesAgentSuggestions
                .map((key) => tUi<string>(key))
                .map((suggestion) => (
                  <Suggestion key={suggestion} disabled={isChatBusy} onClick={() => onSubmitMessage(suggestion)}>
                    {suggestion}
                  </Suggestion>
                ))}
            </Suggestions>
          ) : null}
          {chatError ? (
            <Alert
              type="error"
              showIcon
              message={chatError.message}
              className="mx-auto mb-3 max-w-5xl"
              action={
                <Space>
                  <Button size="small" onClick={onClearError}>
                    Ukryj
                  </Button>
                  <Button size="small" type="primary" onClick={onRetryLastResponse}>
                    Spróbuj ponownie
                  </Button>
                </Space>
              }
            />
          ) : null}
          <PromptInput
            accept="image/*,audio/*,.pdf,.txt,.md,.csv"
            className="relative mx-auto max-w-5xl rounded-[28px] border-gray-200 bg-white/95 shadow-[0_12px_40px_rgba(15,23,42,0.08)] backdrop-blur-xl"
            globalDrop
            maxFiles={5}
            maxFileSize={10 * 1024 * 1024}
            multiple
            onError={(error) => void message.error(error.message)}
            onSubmit={(payload) => onSubmitMessage(payload.text, payload.files)}
          >
            <SalesAgentPromptHeader />
            <PromptInputBody>
              {audioRecordMode ? (
                <div className="px-2 py-2">
                  <SalesAgentVoiceRecorder
                    onCancel={() => setAudioRecordMode(false)}
                    onSend={(file) => {
                      setAudioRecordMode(false);
                      onSubmitMessage("", [file]);
                    }}
                    onNotAllowed={() => {
                      setAudioRecordMode(false);
                      void message.error("Brak dostępu do mikrofonu");
                    }}
                  />
                </div>
              ) : (
                <PromptInputTextarea
                  ref={promptTextareaRef}
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  onKeyDown={handlePromptKeyDown}
                  placeholder="Napisz wiadomość albo wklej screenshot..."
                  disabled={isChatBusy || !isViewingOwnChats}
                />
              )}
            </PromptInputBody>
            <PromptInputFooter>
              <PromptInputTools>
                <PromptInputActionMenu>
                  <PromptInputActionMenuTrigger />
                  <PromptInputActionMenuContent align="start">
                    <PromptInputActionAddAttachments label="Dodaj plik" />
                  </PromptInputActionMenuContent>
                </PromptInputActionMenu>
                <SalesAgentModelPicker
                  disabled={isChatBusy || !isViewingOwnChats}
                  selectedModelId={selectedModelId}
                  onChange={onModelChange}
                />
                <Tooltip title={audioRecordMode ? "Wróć do pisania" : "Nagraj wiadomość głosową"}>
                  <Button
                    size="small"
                    type="text"
                    disabled={isChatBusy || !isViewingOwnChats}
                    icon={<AudioOutlined className={audioRecordMode ? "text-blue-500" : undefined} />}
                    onClick={() => setAudioRecordMode((current) => !current)}
                    aria-label={audioRecordMode ? "Wróć do pisania" : "Nagraj wiadomość głosową"}
                  />
                </Tooltip>
              </PromptInputTools>
              {isChatBusy ? (
                <Button
                  className="h-10 w-10 rounded-full bg-gray-950 text-white hover:bg-gray-800"
                  icon={<StopOutlined />}
                  onClick={onStop}
                >
                  <span className="sr-only">Stop</span>
                </Button>
              ) : (
                // The recorder has its own send button; the regular submit would fire
                // with stale textarea content while recording.
                !audioRecordMode && (
                  <SalesAgentPromptSubmit input={input} status={isViewingOwnChats ? chatStatus : "submitted"} />
                )
              )}
            </PromptInputFooter>
          </PromptInput>
          {!isViewingOwnChats ? (
            <Typography.Text type="secondary" className="mx-auto mt-2 block max-w-4xl text-xs">
              Podgląd rozmów sprzedawcy. Odpowiadać i tworzyć wiadomości może tylko właściciel czatu.
            </Typography.Text>
          ) : null}
        </div>
      </div>
    </Card>
  );
};
